// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Structural inference: what the topology and the labels imply, beyond what
 * the payload states.
 *
 * Sits between `powerFlowGraph` and `powerFlowRank` in the structural half of
 * the model, so it is rebuilt only when the content hash changes. Nothing in
 * here reads a live value — that is `powerFlowLive.ts`.
 *
 * Every inference here is a claim made on the twin's behalf, so each follows
 * the four rules in `docs/power-flow/power-flow-inference.md`: it is derived
 * from something the payload states (never from an absence), it is carried as
 * data with a provenance so the drawing can show it as inferred, its falsifier
 * is a test, and it lives here rather than in a component.
 *
 * What is inferred, and from what:
 *
 * - **Blocks** — assets whose labels share a block letter (`Block-A`,
 *   `SWGR-1-A`, `RPP-PDU-A-1-1`), plus unlabelled assets adopted from the
 *   block of everything that feeds them (a chiller called `ACCH-1` fed only by
 *   `Mechanical-Switchgear-Block-A` is in block A).
 * - **Feeder groups** — which blocks hang off which utility intake, by walking
 *   from each root utility source through transformers to the first boards.
 * - **Source selection** — a board with two or more source-like inputs (a
 *   transformer and a genset) performs the transfer function an `ATSType`
 *   instance would otherwise model.
 * - **Bypass links** — a board-to-board edge parallel to a UPS path is the
 *   maintenance bypass.
 * - **Branch class** — critical or mechanical, from what the branch ultimately
 *   feeds: racks or motors. The label (`Mechanical-`, `IT-`) is recorded as a
 *   confirming signal, never as the deciding one.
 * - **Intake and output boards** — the boards every source-side path lands on
 *   first, and the boards fed by UPSs, which the KPI arithmetic sums over.
 * - **Dual feeds** — how many upstream conductors each terminal load has.
 */

import type { Entity } from '../types/digitalTwin';
import type { PowerFlowGraph, PowerFlowVertex } from './powerFlowGraph';
import { isMotorSymbol } from '../config/powerFlowSymbols';
import { parseQuantity } from './quantity';
import { stripOntologyPrefix } from './entityHelpers';

export type Provenance = 'published' | 'measured' | 'derived' | 'convention';

export interface PowerFlowBlock {
  /** `block:<letter>` */
  id: string;
  letter: string;
  /** Member asset ids, sorted. */
  memberIds: string[];
  /** Members placed by their label, as opposed to adopted from their feeder. */
  labelledCount: number;
}

export interface PowerFlowFeederGroup {
  /** The root utility source the group hangs off (a meter, or a feeder). */
  rootId: string;
  /** Block ids reached from it, sorted. */
  blockIds: string[];
  /** Boards reached from it, sorted. */
  boardIds: string[];
}

export type BranchClass = 'critical' | 'mechanical' | 'mixed' | 'none';

export interface BranchClassification {
  branchClass: BranchClass;
  /** What the label says, when it says anything. */
  labelClass?: 'critical' | 'mechanical';
  /** False when the label and the loads disagree — worth a notice. */
  labelAgrees: boolean;
}

export interface PowerFlowInference {
  blocks: PowerFlowBlock[];
  blockOf: Map<string, string>;
  feederGroups: PowerFlowFeederGroup[];
  /** Block id → feeder-group root id. */
  feederGroupOfBlock: Map<string, string>;
  /** Board id → its source-like inputs, sorted. Only boards with two or more. */
  sourceSelectionOf: Map<string, string[]>;
  /** Link ids that are a maintenance bypass around a UPS path. */
  bypassLinkIds: Set<string>;
  /** The UPS ids each bypass link parallels. */
  bypassedUpsOf: Map<string, string[]>;
  /** Link ids joining two transformers: a loop-through of the MV riser. */
  riserLinkIds: Set<string>;
  branchOf: Map<string, BranchClassification>;
  /** Boards whose every input is source-like — the site intake. Sorted. */
  intakeBoardIds: string[];
  /** Boards with at least one UPS input — the critical output boards. Sorted. */
  outputBoardIds: string[];
  /** Terminal load id → number of upstream conductors. */
  feedCountOf: Map<string, number>;
  /** Terminal loads with exactly one upstream conductor, sorted. */
  singleFedLoadIds: string[];
}

/** Label-derived block letter: `Transformer-Block-A`, `UPS-IT-Block-A-1`. */
const BLOCK_LABEL_RE = /\bBlock[-_ ]([A-Z])(?:[-_ ]|\b|$)/i;
/** Id-derived block letter: `SWGR-1-A`, `RPP-1-A1`, `PB-1-B`. */
const BLOCK_ID_RE = /[-_]([A-Z])\d*$/;
/** `RPP-PDU-A-1-1`. */
const BLOCK_RPP_RE = /^RPP[-_]PDU[-_]([A-Z])[-_]/i;

const MECHANICAL_LABEL_RE = /\bmech(anical)?\b/i;
const CRITICAL_LABEL_RE = /\bIT\b|(^|[-_ ])IT([-_ ]|$)/;

/**
 * Terminal IT distribution: where a critical branch ends when the twin stops
 * above the racks. GA-11 publishes no `ITRackType` at all — its UPS-backed
 * chain ends at 90 RPPs — so a rack-only rule read every IT board as
 * mechanical and flagged its "IT" label as wrong.
 */
const CRITICAL_LEAF_TYPES: ReadonlySet<string> = new Set([
  'RPPType',
  'RackPDUType',
  'PDUType',
  'ITRackType',
  'IT_Rack',
]);

const SOURCE_LIKE_SYMBOLS = new Set(['utility', 'generator', 'battery', 'transformer']);

const upstreamId = (entity: Entity | undefined): string | undefined => {
  const raw = entity?.attributes?.upstreamId;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : undefined;
};

const labelOf = (entity: Entity | undefined): string | undefined => {
  const raw = entity?.attributes?.label;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : undefined;
};

/**
 * The block letter an asset's own naming gives it, or undefined.
 *
 * Exported so the falsifier — a site that does not follow the convention
 * produces no blocks — is testable at the rule rather than the whole pass.
 */
export const blockLetterOf = (entity: Entity | undefined): string | undefined => {
  if (!entity) return undefined;
  const label = labelOf(entity);
  const id = upstreamId(entity);
  for (const candidate of [label, id]) {
    if (!candidate) continue;
    const byLabel = candidate.match(BLOCK_LABEL_RE);
    if (byLabel) return byLabel[1].toUpperCase();
    const byRpp = candidate.match(BLOCK_RPP_RE);
    if (byRpp) return byRpp[1].toUpperCase();
  }
  for (const candidate of [id, label]) {
    if (!candidate) continue;
    const byId = candidate.match(BLOCK_ID_RE);
    if (byId) return byId[1].toUpperCase();
  }
  return undefined;
};

const sorted = (values: Iterable<string>): string[] => Array.from(values).sort();

/**
 * Run the pass.
 *
 * Deterministic: every collection is sorted, and adoption iterates to a
 * fixpoint in vertex order, so the result does not depend on how the payload
 * was paginated.
 */
/** Within this fraction, two rated voltages are the same system. */
const RATING_TOLERANCE = 0.08;

/**
 * True unless both transformers publish a `ratedVoltage` and the two differ.
 * Unrated units are given the benefit of the doubt, as the rest of the
 * drawing does; a stated mismatch is the falsifier.
 */
const compatibleRatings = (a: Entity | undefined, b: Entity | undefined): boolean => {
  const va = parseQuantity(a?.attributes?.ratedVoltage);
  const vb = parseQuantity(b?.attributes?.ratedVoltage);
  if (!va || !vb || va.base <= 0 || vb.base <= 0) return true;
  return Math.abs(va.base - vb.base) / Math.max(va.base, vb.base) <= RATING_TOLERANCE;
};

export const inferPowerFlow = (
  graph: PowerFlowGraph,
  entities: readonly Entity[]
): PowerFlowInference => {
  const entitiesById = new Map(entities.map(entity => [entity.id, entity]));
  const { vertices, verticesById, links } = graph;

  // --- Blocks ------------------------------------------------------------
  const letterOf = new Map<string, string>();
  const labelled = new Set<string>();
  for (const vertex of vertices) {
    const letter = blockLetterOf(entitiesById.get(vertex.id));
    if (letter) {
      letterOf.set(vertex.id, letter);
      labelled.add(vertex.id);
    }
  }

  // Adoption: an unlabelled asset whose every upstream neighbour is in one
  // block belongs to it. Two passes suffice for a chain like
  // `board → chiller`; the loop runs to a fixpoint regardless.
  for (let guard = 0; guard < vertices.length; guard += 1) {
    let changed = false;
    for (const vertex of vertices) {
      if (letterOf.has(vertex.id) || vertex.in.length === 0) continue;
      const parentLetters = new Set(vertex.in.map(parent => letterOf.get(parent)));
      if (parentLetters.size === 1 && !parentLetters.has(undefined)) {
        letterOf.set(vertex.id, Array.from(parentLetters)[0] as string);
        changed = true;
      }
    }
    if (!changed) break;
  }

  const membersByLetter = new Map<string, string[]>();
  for (const [id, letter] of Array.from(letterOf.entries())) {
    const list = membersByLetter.get(letter);
    if (list) list.push(id);
    else membersByLetter.set(letter, [id]);
  }

  // A block is at least two connected assets; a lone letter is a coincidence
  // of naming, not a lineup.
  const blocks: PowerFlowBlock[] = Array.from(membersByLetter.entries())
    .filter(([, members]) => members.length >= 2)
    .map(([letter, members]) => ({
      id: `block:${letter}`,
      letter,
      memberIds: sorted(members),
      labelledCount: members.filter(id => labelled.has(id)).length,
    }))
    .sort((a, b) => (a.letter < b.letter ? -1 : 1));

  const blockOf = new Map<string, string>();
  for (const block of blocks) for (const id of block.memberIds) blockOf.set(id, block.id);

  // --- Intake and output boards ------------------------------------------
  const isSourceLike = (id: string): boolean => {
    const vertex = verticesById.get(id);
    return !!vertex && (vertex.role === 'source' || SOURCE_LIKE_SYMBOLS.has(vertex.symbol));
  };

  const intakeBoardIds = sorted(
    vertices
      .filter(v => v.role === 'bus' && v.in.length > 0 && v.in.every(isSourceLike))
      .map(v => v.id)
  );

  const outputBoardIds = sorted(
    vertices
      .filter(
        v => v.role === 'bus' && v.in.some(parent => verticesById.get(parent)?.symbol === 'ups')
      )
      .map(v => v.id)
  );

  // --- Feeder groups -----------------------------------------------------
  const feederGroups: PowerFlowFeederGroup[] = [];
  const feederGroupOfBlock = new Map<string, string>();
  const roots = vertices.filter(v => v.symbol === 'utility' && v.in.length === 0);
  for (const root of roots) {
    const boards = new Set<string>();
    const seen = new Set<string>();
    const stack = [root.id];
    while (stack.length > 0) {
      const id = stack.pop() as string;
      if (seen.has(id)) continue;
      seen.add(id);
      const vertex = verticesById.get(id);
      if (!vertex) continue;
      if (vertex.role === 'bus') {
        boards.add(id);
        continue;
      }
      if (id === root.id || isSourceLike(id)) stack.push(...vertex.out);
    }
    const blockIds = sorted(
      new Set(
        Array.from(boards)
          .map(b => blockOf.get(b))
          .filter((b): b is string => !!b)
      )
    );
    feederGroups.push({ rootId: root.id, blockIds, boardIds: sorted(boards) });
    for (const blockId of blockIds) {
      // A block reached from two roots keeps the first, in sorted root order.
      if (!feederGroupOfBlock.has(blockId)) feederGroupOfBlock.set(blockId, root.id);
    }
  }
  feederGroups.sort((a, b) => (a.rootId < b.rootId ? -1 : 1));

  // --- Source selection --------------------------------------------------
  const sourceSelectionOf = new Map<string, string[]>();
  for (const vertex of vertices) {
    if (vertex.role !== 'bus') continue;
    const sources = sorted(vertex.in.filter(isSourceLike));
    if (sources.length >= 2) sourceSelectionOf.set(vertex.id, sources);
  }

  // --- Bypass and riser links ---------------------------------------------
  const bypassLinkIds = new Set<string>();
  const bypassedUpsOf = new Map<string, string[]>();
  const riserLinkIds = new Set<string>();
  for (const link of links) {
    const source = verticesById.get(link.source);
    const target = verticesById.get(link.target);
    if (!source || !target) continue;

    // A transformer feeding a transformer is the MV loop passing through —
    // unless their ratings say otherwise. Two units rated for different
    // voltages are a genuine cascade, a transformation path, not a riser.
    if (
      source.symbol === 'transformer' &&
      target.symbol === 'transformer' &&
      compatibleRatings(entitiesById.get(source.id), entitiesById.get(target.id))
    ) {
      riserLinkIds.add(link.id);
    }

    if (source.role === 'bus' && target.role === 'bus') {
      const parallelUps = sorted(
        source.out.filter(mid => verticesById.get(mid)?.symbol === 'ups' && target.in.includes(mid))
      );
      if (parallelUps.length > 0) {
        bypassLinkIds.add(link.id);
        bypassedUpsOf.set(link.id, parallelUps);
      }
    }
  }

  // --- Branch class ------------------------------------------------------
  const branchOf = new Map<string, BranchClassification>();
  const leafClasses = new Map<string, Set<'critical' | 'mechanical'>>();

  /**
   * The leaf classes reachable from `id`, and whether the walk was **cut**:
   * whether it skipped a child because that child was already on the current
   * path (a tie cycle). A cut result is partial — it is missing whatever lies
   * beyond the vertex it refused to re-enter — and it depends on where the
   * walk started, so it must not be memoised. Caching it by id alone made
   * `B` in `A ⇄ B, A → rack` come out `none` or `critical` depending on
   * which of the two the vertex loop happened to visit first.
   */
  const classesBelow = (
    id: string,
    trail: Set<string>
  ): { classes: Set<'critical' | 'mechanical'>; cut: boolean } => {
    const cached = leafClasses.get(id);
    if (cached) return { classes: cached, cut: false };
    const classes = new Set<'critical' | 'mechanical'>();
    const vertex = verticesById.get(id);
    if (!vertex) return { classes, cut: false };
    if (vertex.out.length === 0 || vertex.role === 'load') {
      if (
        vertex.symbol === 'rack' ||
        CRITICAL_LEAF_TYPES.has(stripOntologyPrefix(vertex.entityType))
      ) {
        classes.add('critical');
      }
      if (isMotorSymbol(vertex.symbol)) classes.add('mechanical');
    }
    let cut = false;
    trail.add(id);
    for (const child of vertex.out) {
      if (trail.has(child)) {
        cut = true;
        continue;
      }
      const below = classesBelow(child, trail);
      for (const cls of Array.from(below.classes)) classes.add(cls);
      cut = cut || below.cut;
    }
    trail.delete(id);
    // Only a complete walk is worth remembering; see above.
    if (!cut) leafClasses.set(id, classes);
    return { classes, cut };
  };

  for (const vertex of vertices) {
    const below = classesBelow(vertex.id, new Set()).classes;
    const branchClass: BranchClass =
      below.size === 2
        ? 'mixed'
        : below.size === 1
          ? (Array.from(below)[0] as BranchClass)
          : 'none';

    const label = `${labelOf(entitiesById.get(vertex.id)) ?? ''} ${upstreamId(entitiesById.get(vertex.id)) ?? ''}`;
    const labelClass = MECHANICAL_LABEL_RE.test(label)
      ? 'mechanical'
      : CRITICAL_LABEL_RE.test(label)
        ? 'critical'
        : undefined;

    branchOf.set(vertex.id, {
      branchClass,
      labelClass,
      // A label on a board above the split ("IT-Input") is a naming choice,
      // not a contradiction; only a decided class can disagree with one. A
      // load is not judged: a CRAH named for the IT hall it cools is not wrong.
      labelAgrees:
        vertex.role === 'load' ||
        labelClass === undefined ||
        branchClass === 'mixed' ||
        branchClass === 'none' ||
        labelClass === branchClass,
    });
  }

  // --- Dual feeds --------------------------------------------------------
  const feedCountOf = new Map<string, number>();
  const singleFed: string[] = [];
  for (const vertex of vertices) {
    if (vertex.role !== 'load') continue;
    feedCountOf.set(vertex.id, vertex.in.length);
    if (vertex.in.length === 1) singleFed.push(vertex.id);
  }

  return {
    blocks,
    blockOf,
    feederGroups,
    feederGroupOfBlock,
    sourceSelectionOf,
    bypassLinkIds,
    bypassedUpsOf,
    riserLinkIds,
    branchOf,
    intakeBoardIds,
    outputBoardIds,
    feedCountOf,
    singleFedLoadIds: sorted(singleFed),
  };
};

/** An empty result, for callers with no graph. */
export const EMPTY_INFERENCE: PowerFlowInference = {
  blocks: [],
  blockOf: new Map(),
  feederGroups: [],
  feederGroupOfBlock: new Map(),
  sourceSelectionOf: new Map(),
  bypassLinkIds: new Set(),
  bypassedUpsOf: new Map(),
  riserLinkIds: new Set(),
  branchOf: new Map(),
  intakeBoardIds: [],
  outputBoardIds: [],
  feedCountOf: new Map(),
  singleFedLoadIds: [],
};

/** Convenience: a vertex's class, `none` when unknown. */
export const branchClassOf = (inference: PowerFlowInference, id: string): BranchClass =>
  inference.branchOf.get(id)?.branchClass ?? 'none';

export type { PowerFlowVertex };
